<!-- eslint-disable max-len -->
<template>
  <div>
    <ActivityStopwatch class="mb-5"
                       :onEditClick="editActivity"
                       :onDeleteClick="openDeleteDialog"
                       v-on:activityResumed="activityElapsed = true"
                       v-on="$listeners" />
    <!-- Activities Table -->
    <v-data-table :headers="anyUnsyncedItems ? offlineHeaders : headers"
                  :items="vesselActivities"
                  :items-per-page="15"
                  :single-expand="true"
                  disable-sort
                  show-expand
                  :mobile-breakpoint="dataTableMobileBreakpoint"
                  class="elevation-1"
                  :item-class="customRowClass"
                  :loading="activitiesLoading || ecargoDataLoading">
      <template v-slot:top>
        <div class="d-flex pa-4">
          <span class="headline primary--text font-weight-medium">
            <v-icon class="primary--text" left>mdi-format-list-bulleted-square</v-icon>Vessel Activity
          </span>
          <v-spacer></v-spacer>
          <v-dialog v-model="formDialog" persistent max-width="850px">
            <template v-slot:activator="{ on }">
              <v-btn :disabled="disableAddButton || ecargoDataLoading"
                     v-bind="headerButtonSize"
                     color="primary"
                     v-on="on">
                <v-icon left>mdi-plus</v-icon>Add
              </v-btn>
              <div class="hidden-md-and-down export-btn-container">
                <v-btn :disabled="disableAddConsumptionButton"
                       :loading="submittingConsumptionActivity"
                       v-bind="headerButtonSize"
                       color="primary"
                       class="mr-2"
                       @click="createVesselConsumptionActivityForDay">
                  <v-icon left>mdi-gauge</v-icon>Record Consumption
                </v-btn>
                <csv-download-button class="mx-2"
                                     :size="headerButtonSize"
                                     :data="vesselActivities"
                                     :fields="csvExportFields"
                                     :labels="csvExportLabels"
                                     :fileName="getExportFileName()" />
                <excel-download-button v-if="isClientConnectedToServer"
                                       :size="headerButtonSize"
                                       :onClick="exportAsExcel"
                                       :isLoading="isExportingExcel" />
              </div>
              <!-- Mobile/Tablet menu for Excel/Csv exports -->
              <v-menu offset-y left>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on"
                         v-bind="headerButtonSize"
                         class="mr-2 hidden-lg-and-up">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item :disabled="
                      disableAddConsumptionButton ||
                      submittingConsumptionActivity
                    "
                               @click="createVesselConsumptionActivityForDay">
                    <v-list-item-icon><v-icon>mdi-gauge</v-icon></v-list-item-icon>
                    <v-list-item-title class="text-capitalize">
                      Record Consumption
                    </v-list-item-title>
                  </v-list-item>
                  <download-csv :data="vesselActivities"
                                :name="getExportFileName()"
                                :fields="csvExportFields"
                                :labels="csvExportLabels">
                    <v-list-item style="cursor: pointer">
                      <v-list-item-icon>
                        <v-icon>mdi-file-table</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title class="text-capitalize">
                        Csv Export
                      </v-list-item-title>
                    </v-list-item>
                  </download-csv>
                  <v-list-item v-if="isClientConnectedToServer"
                               @click="exportAsExcel()">
                    <v-list-item-icon>
                      <v-icon>mdi-file-excel</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="text-capitalize">
                      Excel Export
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <v-card>
              <VesselActivityForm ref="activityForm"
                                  :isOpen="formDialog"
                                  :vesselActivity="selectedActivity"
                                  :previousActivity="previousActivity"
                                  :edit="editIndex == -1 ? false : true"
                                  :onCloseClick="closeFormDialog"
                                  :onSubmitClick="saveActivity"
                                  :isSubmitting="isActivitySubmitting"
                                  :bus="bus"
                                  :vesselActivities="vesselActivities"/>
            </v-card>
          </v-dialog>
        </div>
      </template>
      <template v-slot:item.syncState="{ item }">
        <SyncStateIcon v-if="anyUnsyncedItems"
                       :isItemUnsynced="isItemUnsynced(item)" />
      </template>
      <template v-slot:item.startDateTimeLt="{ item }">
        <span v-if="!item.startDateTimeLt"></span>
        <span else>
          {{
          item.startDateTimeLt | removeTAndSecondsAndFormatDate
          }}
        </span>
      </template>
      <template v-slot:item.endDateTimeLt="{ item }">
        <span v-if="!item.endDateTimeLt">Ongoing</span>
        <span else>
          {{
          item.endDateTimeLt | removeTAndSecondsAndFormatDate
          }}
        </span>
      </template>
      <template v-slot:item.deckSpaceUsed="{ item }">
        <span>{{ item.deckSpaceUsed }}%</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex">
          <v-badge v-if="shouldShowCargoIcon(item)"
                   :value="activityHasCargoItems(item)"
                   dot
                   color="success"
                   :class="isBulkActivity(item) && 'cargo-icon'"> 
            <v-icon :size="iconSize"
                    @click="prepareCargoDialog(item)"
                    :disabled="ecargoDataLoading"
                    class="cargo-handling-icon">
              $vuetify.icons.cargo
            </v-icon>
          </v-badge>
          <v-badge v-if="isBulkActivity(item)"
                   :value="activityHasBulkTransactions(item)"
                   dot
                   color="success">
            <v-icon v-if="item.code !== '03'"
                    :class="(!isBulkActivity(item) && 'hidden') || (shouldShowCargoIcon(item) && 'bulk-icon')"
                    :size="iconSize"
                    @click="openBulkDialog(item)">mdi-handshake</v-icon>
            <v-icon v-else
                    :size="iconSize"
                    @click="openAddConsumptionActivityDialog(item)">mdi-gauge</v-icon>
          </v-badge>
          <v-icon class="mx-3"
                  :size="iconSize"
                  @click="editActivity(item)"
                  :disabled="disableEditButtonForItem(item)">mdi-pencil</v-icon>
          <v-icon :size="iconSize"
                  @click.stop="openDeleteDialog(item)"
                  :disabled="(!isClientConnectedToServer && (!item.idc || item.id !== undefined)) || ecargoDataLoading">mdi-delete</v-icon>
        </div>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="text-start pa-8">
          <ul>
            <li>
              <span class="font-weight-bold">Activity Information:</span>
              <span class="mx-2">{{ item.activityInfo }}</span>
            </li>
            <li>
              <span class="font-weight-bold">Distance Sailed (nm):</span>
              <span class="mx-2">{{ item.distanceSailed }}</span>
            </li>
            <li>
              <span class="font-weight-bold">Vessel Comment:</span>
              <span>{{ item.vesselComment }}</span>
            </li>
            <li>
              <span class="font-weight-bold">Verification Comment:</span>
              <span>{{ item.verificationComment }}</span>
            </li>
          </ul>
        </td>
      </template>
    </v-data-table>
    <bulk-transaction-dialog v-model="showBulkDialog"
                             :activity="selectedBulkActivity"
                             :onCloseClick="closeBulkDialog"
                             v-on:refreshSelectedBulkActivity="resetSelectedBulkActivity" />
    <cargo-handling-dialog v-model="showCargoDialog"
                             :activity="selectedCargoActivity"
                             :outboundCargo="outboundCargo.length > 0"
                             :inboundCargo="inboundCargo.length > 0"
                             :error="cargoHandlingError"
                             :onCloseClick="closeCargoDialog" /> 
    <VesselConsumptionActivityDialog v-if="selectedConsumptionActivity"
                                     v-model="showAddConsumptionDialog"
                                     :activity="selectedConsumptionActivity"
                                     v-on:refreshSelectedConsumptionActivity="resetSelectedConsumptionActivity" />
    <ConfirmDialog v-model="showDeleteDialog"
                   title="Delete Activity"
                   description="Are you sure you want to delete this activity?"
                   :onConfirmClick="deleteActivity"
                   :onCloseClick="closeDeleteDialog" />
  </div>
</template>

<script>
  import Vue from 'vue';
  import { mapState, mapGetters } from 'vuex';
  import DownloadHelperMixin from '@/mixins/DownloadHelperMixin';
  import ResponsiveSizeHelperMixin from '@/mixins/ResponsiveSizeHelperMixin';
  import VesselActivityHelperMixin from '@/mixins/VesselActivityHelperMixin';
  import dateTimeHelpers from '../../mixins/DateTimeHelpers';
  import ExcelDownloadButton from '../ui/ExcelDownloadButton.vue';
  import CsvDownloadButton from '../ui/CsvDownloadButton.vue';
  import ActivityStopwatch from './ActivityStopwatch.vue';
  import VesselActivityForm from './VesselActivityForm.vue';
  import BulkTransactionDialog from '../bulk_transactions/BulkTransactionsDialog.vue';
  import ConfirmDialog from '../ui/ConfirmDialog.vue';
  import SyncStateIcon from '../ui/SyncStateIcon.vue';
  import VesselConsumptionActivityDialog from '../vessel-consumption/VesselConsumptionActivityDialog.vue';
  import { BulkTransactionCreateModel } from '../../models/bulk-transaction/BulkTransactionCreateModel';
  import CargoHandlingDialog from '../cargo/CargoHandlingDialog.vue';
  import CargoHandlingIcon from '../ui/CargoHandlingIcon.vue';

  /* eslint-disable max-len */

  export default {
    name: 'Activity',
    props: {
      voyageReportId: String,
      voyageReportIdc: String,
      ecargoDataLoading: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      ExcelDownloadButton,
      CsvDownloadButton,
      ActivityStopwatch,
      VesselActivityForm,
      ConfirmDialog,
      BulkTransactionDialog,
      SyncStateIcon,
      VesselConsumptionActivityDialog,
      CargoHandlingDialog,
      CargoHandlingIcon,
    },
    mixins: [DownloadHelperMixin, ResponsiveSizeHelperMixin, VesselActivityHelperMixin],
    data() {
      return {
        shouldOpenBulkTransactionAfterCargoHandling: false,
        formDialog: false,
        showDeleteDialog: false,
        showBulkDialog: false,
        showCargoDialog: false,
        showAddConsumptionDialog: false,
        consumptionDate: dateTimeHelpers.getCurrentDateLt(),
        consumptionActivityCode: '03',
        selectedActivity: {},
        selectedBulkActivity: {},
        selectedCargoActivity: {},
        selectedConsumptionActivity: null,
        inboundCargo: [],
        outboundCargo: [],
        cargoHandlingError: false,
        previousActivity: null,
        activityElapsed: false,
        editIndex: -1,
        activitiesLoading: false,
        isActivitySubmitting: false,
        isExportingExcel: false,
        submittingConsumptionActivity: false,
        headers: [
          { text: 'Start (LT)', value: 'startDateTimeLt' },
          { text: 'End (LT)', value: 'endDateTimeLt' },
          { text: 'Activity', value: 'activityName' },
          { text: 'Code', value: 'code' },
          { text: 'Activity Type', value: 'activityType' },
          { text: 'Location', value: 'locationName' },
          { text: 'Used Deckspace (%)', value: 'deckSpaceUsed' },
          { text: 'Actions', value: 'actions', sortable: false },
          { text: '', value: 'data-table-expand' },
        ],
        csvExportFields: [
          'startDateTimeLt',
          'endDateTimeLt',
          'activityName',
          'code',
          'activityType',
          'activityInfo',
          'locationName',
          'distanceSailed',
          'deckSpaceUsed',
          'vesselComment',
          'verificationComment',
        ],
        csvExportLabels: {
          startDateTimeLt: 'Start DateTime (LT)',
          endDateTimeLt: 'End DateTime (LT)',
          activityName: 'Activity Name',
          code: 'Code',
          activityType: 'Activity Type',
          activityInfo: 'Activity Info',
          locationName: 'Location',
          distanceSailed: 'Diatance Sailed (NM)',
          deckSpaceUsed: 'Deckspace Used (%)',
          vesselComment: 'Vessel Comments',
          verificationComment: 'Verification Comments',
        },
        bus: new Vue(),
      };
    },
    created() {
      if (this.voyageReportId) {
        this.activitiesLoading = true;
        this.$store.dispatch('voyageReportsStore/loadVoyageVesselActivities', this.voyageReportId)
          .then(() => {
            this.$store.dispatch('dataSyncStore/loadVesselActivitiesToBeSynced', this.voyageReportId);
            this.$store.dispatch('voyageReportsStore/loadVesselActivitiesCargo', this.voyageReportId);
          })
          .finally(() => { this.activitiesLoading = false; });
      }
      if (this.voyageReportIdc) {
        this.activitiesLoading = true;
        this.$store.dispatch('dataSyncStore/loadVesselActivitiesToBeSynced', this.voyageReportIdc)
          .finally(() => { this.activitiesLoading = false; });
      }
      this.$store.dispatch('activitiesStore/loadAvailableActivities', this.activeSiteId);
      this.$store.dispatch('locationsStore/loadVesselLocations', this.activeSiteId);
      this.$store.dispatch('dataSyncStore/loadAllBulkTransactionsToBeSynced');
    },
    computed: {
      ...mapState(['voyageReportsStore', 'dataSyncStore', 'userStore', 'locationsStore', 'activitiesStore']),
      ...mapGetters({
        getActiveSite: 'userStore/getActiveSite',
        isClientConnectedToServer: 'userStore/isClientConnectedToServer',
        getSyncedAndUnSyncedVesselActivities: 'voyageReportsStore/getSyncedAndUnSyncedVesselActivities',
        getAllUnsyncedBulkTransactions: 'dataSyncStore/getAllUnsyncedBulkTransactions',
        getActivityById: 'activitiesStore/getActivityById',
        getActiveSiteConfig: 'userStore/getActiveSiteConfig',
        getSyncedAndUnSyncedCargoForLocation: 'voyageReportsStore/getSyncedAndUnSyncedCargoForLocation',
      }),
      offlineHeaders() {
        return [
          { text: 'Is Synced', value: 'syncState' },
          ...this.headers,
        ];
      },
      vesselActivities() {
        return this.getSyncedAndUnSyncedVesselActivities;
      },
      voyageReport() {
        return this.voyageReportsStore ? this.voyageReportsStore.voyageReportDetailsData : {};
      },
      activities() {
        return this.activitiesStore ? this.activitiesStore.activities : null;
      },
      activeSiteId() {
        const site = this.getActiveSite;
        if (!site) {
          return null;
        }
        return site.id;
      },
      activeSiteConfig() {
        const siteConfig = this.getActiveSiteConfig;
        if (!siteConfig) {
          return null;
        }
        return siteConfig;
      },
      disableAddButton() {
        const voyageStatus = this.voyageReport ? this.voyageReport.status : 'NotSet';
        const result = (this.activityElapsed || voyageStatus === 'Pending');
        return result;
      },
      disableAddConsumptionButton() {
        const voyageStatus = this.voyageReport ? this.voyageReport.status : 'NotSet';
        const result = (voyageStatus === 'Pending');
        return result;
      },
      unsyncedBulkTransactionsForVoyage() {
        return this.getAllUnsyncedBulkTransactions
          .filter((x) => this.voyageReport && x.voyageReportId === this.voyageReport.id);
      },
      anyUnsyncedVesselActivities() {
        return this.getSyncedAndUnSyncedVesselActivities.some((x) => (x && x.idc));
      },
      anyUnsyncedBulkTransactionsForVoyage() {
        return this.unsyncedBulkTransactionsForVoyage.length > 0;
      },
      anyUnsyncedItems() {
        return this.anyUnsyncedVesselActivities || this.anyUnsyncedBulkTransactionsForVoyage;
      },
      loading() {
        return this.activitiesLoading && this.ecargoDataLoading;
      }
    },
    watch: {
      vesselActivities(val) {
        this.checkCurrentActivityElapsed();
        this.setNewActivityDefaultValues(val);
      },
      voyageReport() {
        this.setNewActivityDefaultValues(this.vesselActivities);
      },
    },
    methods: {
      closeFormDialog() {
        this.editIndex = -1;

        this.$nextTick(() => {
          this.formDialog = false;
          this.setNewActivityDefaultValues(this.vesselActivities);
          this.bus.$emit('resetForm');
        });
      },
      saveActivity() {
        if (this.editIndex > -1) {
          this.updateVesselActivity();
        } else {
          this.createVesselActivity();
        }
      },
      editActivity(selectedActivity) {
        this.editIndex = this.vesselActivities.indexOf(selectedActivity);

        this.selectedActivity = { ...selectedActivity };

        if (selectedActivity.activity != null) this.selectedActivity.activityId = selectedActivity.activity.id;

        this.setActivityDateAndTimeValues();
        this.formDialog = true;
      },
      updateVesselActivity() {
        this.isActivitySubmitting = true;
        this.setActivityDateTime();

        const updatedActivity = {
          ...this.selectedActivity,
          locationName: this.selectedActivity.location.name,
          distanceSailed: parseInt(this.selectedActivity.distanceSailed, 10),
          deckSpaceUsed: parseInt(this.selectedActivity.deckSpaceUsed, 10),
        };

        this.$store
          .dispatch('voyageReportsStore/updateVesselActivity', updatedActivity)
          .then((resp) => {
            this.onSuccess('Vessel Activity Updated', resp);
          })
          .then(() => {
            // When consumption activity has its times changed
            // we now need to update any bulk transactions and
            // associated tank statuses to match the new times.
            this.updateBulkTransactionsAndTankStatuses(updatedActivity);
          })
          .catch((err) => {
            this.onError('Failed to update Vessel Activity');
            console.error(err);
          })
          .finally(() => { this.isActivitySubmitting = false; });
      },
      updateBulkTransactionsAndTankStatuses(activity) {

        const { bulkTransactions } = activity;

        const updates = bulkTransactions.map(transaction => {

          if (transaction.transactionType.toLowerCase() !== "consumption") {
            return;
          }

          const previousVersionId = transaction.id;

          const bulkTransactionCreateModel = new BulkTransactionCreateModel(
            transaction.siteId,
            activity.endDateTimeLt,
            "Consumption",
            transaction.vesselName,
            activity.locationName,
            transaction.bulk,
            transaction.quantity,
            transaction.qtyUom,
            transaction.bulkDeliveryNoteNumber,
            transaction.comments,
            activity.id,
            transaction.tank.id,
            transaction.idc);

          bulkTransactionCreateModel.previousVersionId = previousVersionId;

          return this.$store.dispatch('bulkTransactionsStore/createBulkTransaction', bulkTransactionCreateModel)
            .then((transaction) => {
              this.onSuccess('Bulk Consumption Transaction Created', transaction);
            })
            .catch((err) => this.onError('Failed to Edit Bulk Consumption Transaction', err));

        });

        return Promise.all(updates);

      },
      createVesselActivity() {
        this.isActivitySubmitting = true;
        this.setActivityDateTime();

        const { activityId } = this.selectedActivity;
        const activity = this.getActivityById(activityId);

        const createdActivity = {
          siteId: this.activeSiteId,
          voyageReportId: this.voyageReport.id ?? undefined,
          vesselName: this.voyageReport.vesselName,
          voyageNo: this.voyageReport.voyageNo,
          startDateTimeLt: this.selectedActivity.startDateTimeLt,
          endDateTimeLt: this.selectedActivity.endDateTimeLt,
          activityName: this.selectedActivity.activityName,
          code: this.selectedActivity.code,
          activityType: this.selectedActivity.activityType,
          activityInfo: this.selectedActivity.activityInfo,
          activityId: this.selectedActivity.activityId,
          activity,
          location: this.selectedActivity.location,
          locationName: this.selectedActivity.location.name,
          distanceSailed: this.selectedActivity.distanceSailed
            ? parseInt(this.selectedActivity.distanceSailed, 10) : 0,
          deckSpaceUsed: parseInt(this.selectedActivity.deckSpaceUsed, 10),
          vesselComment: this.selectedActivity.vesselComment,
          verificationComment: this.selectedActivity.verificationComment,
          bulkTransactions: [],
          voyageReportIdc: this.voyageReport.idc,
        };

        this.$store
          .dispatch('voyageReportsStore/createVesselActivity', createdActivity)
          .then((vesselActivity) => {
            this.onSuccess('Vessel Activity Created', vesselActivity);
            this.$nextTick(() => {
              const isCargoActivity = this.isCargoActivity(vesselActivity);
              const isBulkActivity = this.isBulkActivity(vesselActivity);
              const isDischargeActivity = this.isDischargeActivity(vesselActivity.code);
              const isOffshoreActivity = this.isOffshoreActivity(vesselActivity);
              if (isCargoActivity && isOffshoreActivity && this.activeSiteConfig.isECargoEnabled) {
                this.shouldOpenBulkTransactionAfterCargoHandling = true;
                this.prepareCargoDialog(vesselActivity);
              }
              else if (isBulkActivity) {
                this.openBulkDialog(vesselActivity);
              } else if (isDischargeActivity
                && vesselActivity.endDateTimeLt !== null
                && this.isClientConnectedToServer) {
                this.emitEndVoyageDischargeEvent();
              }
            });
          })
          .catch((err) => {
            this.onError('Failed to created Vessel Activity');
            console.error(err);
          })
          .finally(() => { this.isActivitySubmitting = false; });
      },
      openDeleteDialog(item) {
        this.selectedActivity = { ...item };
        this.showDeleteDialog = true;
      },
      closeDeleteDialog() {
        this.selectedActivity = {};
        this.showDeleteDialog = false;
        this.checkCurrentActivityElapsed();
      },
      deleteActivity() {
        const { id, idc } = this.selectedActivity;
        this.$store
          .dispatch('voyageReportsStore/deleteVesselActivity', { id, idc })
          .then((resp) => {
            this.closeDeleteDialog();
            this.onSuccess('Vessel Activity deleted', resp);
          })
          .catch((err) => {
            this.onError('Failed to delete Vessel Activity');
            console.error(err);
          });
      },
      setNewActivityDefaultValues(activities) {
        this.selectedActivity = {};
        const filteredActivities = activities.filter((x) => x.code !== this.consumptionActivityCode);
        if (filteredActivities && filteredActivities.length > 0) {
          const prevActivity = filteredActivities[0];
          this.previousActivity = prevActivity;
          this.selectedActivity.deckSpaceUsed = prevActivity.deckSpaceUsed;
          this.selectedActivity.startDateTimeLt = prevActivity.endDateTimeLt
            ?? dateTimeHelpers.getNowAsLocalISO();
        } else {
          this.selectedActivity.startDateTimeLt = this.voyageReport
            ? this.voyageReport.voyageStartDateTimeLt
            : null;

          this.selectedActivity.deckSpaceUsed = 0;
        }
        this.setActivityDateAndTimeValues();
      },
      setActivityDateTime() {
        const { startTime, endTime } = this.selectedActivity;

        this.selectedActivity.startDateTimeLt = dateTimeHelpers
          .dateAndTimeLtString(this.selectedActivity.startDate, startTime);

        if (this.selectedActivity.endDate) {
          this.selectedActivity.endDateTimeLt = dateTimeHelpers
            .dateAndTimeLtString(this.selectedActivity.endDate, endTime);
        }
      },
      setActivityDateAndTimeValues() {
        // Set individual Date and Time input values for form
        const { startDateTimeLt } = this.selectedActivity;
        const { endDateTimeLt } = this.selectedActivity;

        if (startDateTimeLt) {
          this.selectedActivity.startDate = this.$options.filters.removeTAndTime(startDateTimeLt);
          this.selectedActivity.startTime = this.$options.filters.removeTAndDateAndSeconds(startDateTimeLt);
        }

        if (endDateTimeLt) {
          this.selectedActivity.endDate = this.$options.filters.removeTAndTime(endDateTimeLt);
          this.selectedActivity.endTime = this.$options.filters.removeTAndDateAndSeconds(endDateTimeLt);
        }
      },
      getExportFileName() {
        const siteId = this.voyageReport ? this.voyageReport.siteId : '';
        const voyageNo = this.voyageReport ? this.voyageReport.voyageNo : '';
        const dateTime = dateTimeHelpers.getFileTimestampNow();
        return `${siteId}_VoyageNo-${voyageNo}_VesselActivities-${dateTime}.csv`;
      },
      exportAsExcel() {
        this.isExportingExcel = true;
        this.downloadFile('voyageReportsStore/getVoyageReportAsExcelFile', this.voyageReport.id)
          .finally(() => { this.isExportingExcel = false; });
      },
      checkCurrentActivityElapsed() {
        // Set activityElapsed bool to true if current activity does not have an end time
        const [currentActivity] = this.vesselActivities;
        if (currentActivity) {
          this.activityElapsed = !currentActivity.endDateTimeLt;
        } else {
          this.activityElapsed = false;
        }
      },
      disableEditButtonForItem(item) {
        // return !this.isClientConnectedToServer
        //   || !item.endDateTimeLt;
        return !item.endDateTimeLt || this.ecargoDataLoading;
      },
      customRowClass(item) {
        let rowClass = '';
        if ((this.anyUnsyncedItems) && item) {
          rowClass = (this.isItemUnsynced(item)) ? 'green lighten-4' : 'blue-grey lighten-4';
        }
        return rowClass;
      },
      isItemUnsynced(item) {
        const anyUnsyncedTransactionsForActivity = this.unsyncedBulkTransactionsForVoyage
          .some((x) => x.vesselActivityId === item.id);
        const itemHasIdc = !!item.idc;

        return itemHasIdc || anyUnsyncedTransactionsForActivity;
      },
      isBulkActivity(vesselActivity) {
        const { activity } = vesselActivity;
        const isBulkActivity = activity ? activity.isBulkTransactionActivity : false;
        return isBulkActivity || vesselActivity.code === '03';
      },
      isCargoActivity(vesselActivity) {
        const { activity } = vesselActivity;
        const isCargoActivity = activity ? activity.isCargoHandlingActivity : false;
        return isCargoActivity;
      },
      isOffshoreActivity(vesselActivity) {
        const { activity } = vesselActivity;
        const isOffshoreActivity = activity ? activity.locationType.toLowerCase() === 'offshore' : false;
        return isOffshoreActivity;
      },
      activityHasBulkTransactions(vesselActivity) {
        const { bulkTransactions } = vesselActivity;
        return bulkTransactions ? bulkTransactions.length > 0 : false;
      },
      openBulkDialog(activity) {
        this.selectedBulkActivity = { ...activity };
        this.$store.dispatch('dataSyncStore/loadVesselActivityBulkTransactionsToBeSynced', this.selectedBulkActivity.id);
        this.$nextTick(() => {
          this.showBulkDialog = true;
        });
      },
      closeBulkDialog() {
        this.showBulkDialog = false;
      },
      openCargoDialog(activity, inboundCargo, outboundCargo, isError) {
        this.selectedCargoActivity = { ...activity };
        this.inboundCargo = inboundCargo;
        this.outboundCargo = outboundCargo;
        this.cargoHandlingError = isError;
        const voyageReportId = activity.voyageReport ? activity.voyageReport.id : activity.voyageReportId;
        this.$store.dispatch('dataSyncStore/loadCargoLinesToBeSyncedForVoyage', voyageReportId);
        this.$nextTick(() => {
          this.showCargoDialog = true;
        });
      },
      prepareCargoDialog(vesselActivity) {
        var voyageFoundInFlow = this.voyageReportsStore.eCargoVoyageCargo ? (this.voyageReportsStore.eCargoVoyageCargo.inbound != null || this.voyageReportsStore.eCargoVoyageCargo.outbound != null) : false;
        if (!voyageFoundInFlow) {
          this.openCargoDialog(vesselActivity, [], [], true);
        }
        else {
          if (this.voyageReportsStore.eCargoVoyageCargo.outbound != null && this.voyageReportsStore.eCargoVoyageCargo.outbound.cargoLines != null) {
            var outboundCargo = this.voyageReportsStore.eCargoVoyageCargo.outbound.cargoLines.filter((x) => {
              return (x.destination.toLowerCase() === vesselActivity.locationName.toLowerCase())
            });
          } else {
            var outboundCargo = [];
          }
          if (this.voyageReportsStore.eCargoVoyageCargo.inbound != null && this.voyageReportsStore.eCargoVoyageCargo.inbound.cargoLines != null) {
            var inboundCargo = this.voyageReportsStore.eCargoVoyageCargo.inbound.cargoLines.filter((x) => {
              return (x.destination.toLowerCase() === vesselActivity.locationName.toLowerCase())
            });
          } else {
            var inboundCargo = [];
          }
          if (outboundCargo.length > 0 || inboundCargo.length > 0) {
            this.openCargoDialog(vesselActivity, inboundCargo, outboundCargo, false);
          }
        }
      },
      closeCargoDialog(activity) {
        this.showCargoDialog = false;
        const isBulkActivity = this.isBulkActivity(activity);
        const isDischargeActivity = this.isDischargeActivity(activity);
        if (isBulkActivity && this.shouldOpenBulkTransactionAfterCargoHandling) {
          this.openBulkDialog(activity);
          this.shouldOpenBulkTransactionAfterCargoHandling = false;
        } else if (isDischargeActivity
          && activity.endDateTimeLt !== null
          && this.isClientConnectedToServer) {
          this.emitEndVoyageDischargeEvent();
        }
      },
      shouldShowCargoIcon(activity) {
        var voyageFoundInFlow = this.voyageReportsStore.eCargoVoyageCargo ? (this.voyageReportsStore.eCargoVoyageCargo.inbound != null|| this.voyageReportsStore.eCargoVoyageCargo.outbound != null) : false;
        const isCargoActivity = this.isCargoActivity(activity);
        const isOffshoreActivity = this.isOffshoreActivity(activity);

        const isCargoAndOffshoreActivityFlowEnabled = isCargoActivity && isOffshoreActivity && this.activeSiteConfig.isECargoEnabled;
        if (!isCargoAndOffshoreActivityFlowEnabled) {
          return false;
        }

        if (voyageFoundInFlow && isCargoAndOffshoreActivityFlowEnabled) {
          return true;
        }

        if (this.voyageReportsStore.eCargoVoyageCargo.outbound != null && this.voyageReportsStore.eCargoVoyageCargo.outbound.cargoLines != null) {
          var outboundCargo = this.voyageReportsStore.eCargoVoyageCargo.outbound.cargoLines.filter((x) => {
            return (x.destination.toLowerCase() === activity.locationName.toLowerCase())
          });
        } else {
          var outboundCargo = [];
        }

        if (this.voyageReportsStore.eCargoVoyageCargo.inbound != null && this.voyageReportsStore.eCargoVoyageCargo.inbound.cargoLines != null) {
          var inboundCargo = this.voyageReportsStore.eCargoVoyageCargo.inbound.cargoLines.filter((x) => {
            return (x.destination.toLowerCase() === activity.locationName.toLowerCase())
          });
        } else {
          var inboundCargo = [];
        }

        return isCargoAndOffshoreActivityFlowEnabled && (outboundCargo.length > 0 || inboundCargo.length > 0);
      },
      activityHasCargoItems(activity) {
        var voyageFoundInFlow = this.voyageReportsStore.eCargoVoyageCargo ? (this.voyageReportsStore.eCargoVoyageCargo.inbound != null || this.voyageReportsStore.eCargoVoyageCargo.outbound != null) : false;
        const isCargoActivity = this.isCargoActivity(activity);
        const isOffshoreActivity = this.isOffshoreActivity(activity);

        const isCargoAndOffshoreActivityFlowEnabled = isCargoActivity && isOffshoreActivity && this.activeSiteConfig.isECargoEnabled;
        if (!isCargoAndOffshoreActivityFlowEnabled) {
          return false;
        }

        if (!voyageFoundInFlow && isCargoAndOffshoreActivityFlowEnabled) {
          return false;
        }

        if (this.voyageReportsStore.eCargoVoyageCargo.outbound != null && this.voyageReportsStore.eCargoVoyageCargo.outbound.cargoLines != null) {
          var outboundCargo = this.filterCargoItems(this.getSyncedAndUnSyncedCargoForLocation(true, activity.locationName), activity);
        } else {
          var outboundCargo = [];
        }
        if (this.voyageReportsStore.eCargoVoyageCargo.inbound != null && this.voyageReportsStore.eCargoVoyageCargo.inbound.cargoLines != null) {
          var inboundCargo = this.filterCargoItems(this.getSyncedAndUnSyncedCargoForLocation(false, activity.locationName), activity);
        } else {
          var inboundCargo = [];
        }

        return outboundCargo.length > 0 || inboundCargo.length > 0;

      },
      filterCargoItems(cargoItems, activity) {
        if (cargoItems != null) {
          return cargoItems.filter((x) => {
            return ((x.activityId !== null && x.activityId === activity.id) || (x.activityIdc != undefined && activity.idc != null && x.activityIdc === activity.idc)) &&
              x.markedAsLifted && !x.isNob;
          });
        } else {
          return [];
        }
      },
      resetSelectedBulkActivity(id) {
        const refreshSelectedBulkActivity = this.vesselActivities
          .find((x) => (x.id
            ? x.id === id
            : x.idc.toString() === id.toString()));

        if (refreshSelectedBulkActivity) {
          this.selectedBulkActivity = { ...refreshSelectedBulkActivity };
          if (this.selectedBulkActivity.id) {
            this.$store.dispatch('dataSyncStore/loadVesselActivityBulkTransactionsToBeSynced', this.selectedBulkActivity.id);
          }
        }
      },
      resetSelectedConsumptionActivity(id) {
        console.log("RESET SELECTED CONUMPTION");

        const refreshedConsumptionActivity = this.vesselActivities
          .find((x) => (x.id
            ? x.id === id
            : x.idc.toString() === id.toString()));

        if (refreshedConsumptionActivity) {
          this.selectedConsumptionActivity = { ...refreshedConsumptionActivity };
          if (this.selectedConsumptionActivity.id) {
            this.$store.dispatch('dataSyncStore/loadVesselActivityBulkTransactionsToBeSynced', this.selectedConsumptionActivity.id);
          }
        }
      },
      openAddConsumptionActivityDialog(activity) {
        console.log("OPEN DIAG GOR ADD CONSUMPTION")

        this.selectedConsumptionActivity = { ...activity };
        this.$nextTick(() => {
          this.showAddConsumptionDialog = true;
        });
      },
      createVesselConsumptionActivityForDay() {
        this.submittingConsumptionActivity = true;

        const consumptionExistsForDay = this.doesConsumptionActivityExistForDay();
        if (consumptionExistsForDay) {
          this.onError('Consumption already recorded for day');
          this.submittingConsumptionActivity = false;
          return;
        }

        //// Start Date of consumption
        //// - If voyage start is today, use voyage start
        //// - If today is not voyage start, use start of today
        //// - If voyage end is set and is today, use voyage end
        //// - If voyage end is not set use end of current day

        //const currentDateLt = dateTimeHelpers.getCurrentDateLt();
        //const voyageStartDateLt = this.voyageReport.voyageStartDateTimeLt;
        //const voyageEndDateLt = this.voyageReport.voyageEndDateTimeLt;

        //// Start
        //const isVoyageStartToday =
        //  dateTimeHelpers.getDateStringFromDateTime(voyageStartDateLt) === currentDateLt;

        //const startDateTimeLt = isVoyageStartToday ?
        //  this.voyageReport.voyageStartDateTimeLt
        //  : dateTimeHelpers.startOfToday();

        //// End
        //const isVoyageEndToday =
        //  voyageEndDateLt
        //  && (dateTimeHelpers.getDateStringFromDateTime(voyageEndDateLt) === currentDateLt);

        //const endDateTimeLt = isVoyageEndToday ?
        //  this.voyageReport.voyageEndDateTimeLt
        //  : dateTimeHelpers.endOfToday();

        const currentDateLt = dateTimeHelpers.getNowAsLocalISO();
        const startDateTimeLt = currentDateLt;
        const endDateTimeLt = currentDateLt;

        const activityLocation = this.previousActivity
          ? this.previousActivity.location
          : this.voyageReport.initialPortLocation;

        const consumptionActivity = {
          siteId: this.activeSiteId,
          voyageReportId: this.voyageReport.id ?? undefined,
          vesselName: this.voyageReport.vesselName,
          voyageNo: this.voyageReport.voyageNo,
          activityName: 'Base Activity',
          code: '03',
          activityType: 'Vessel Consumption',
          location: activityLocation,
          locationName: activityLocation.name,
          distanceSailed: 0,
          deckSpaceUsed: 0,
          voyageReportIdc: this.voyageReport.idc,
          startDateTimeLt: startDateTimeLt,
          endDateTimeLt: endDateTimeLt,
          bulkTransactions: [],
        };

        this.$store
          .dispatch('voyageReportsStore/createVesselActivity', consumptionActivity)
          .then((vesselActivity) => {
            this.onSuccess('Vessel Activity Created', vesselActivity);
          })
          .catch((err) => {
            this.onError('Failed to created Vessel Activity');
          })
          .finally(() => { this.submittingConsumptionActivity = false; });
      },
      doesConsumptionActivityExistForDay() {
        const isoDate = dateTimeHelpers.getCurrentDateLt();
        return this.vesselActivities
          .some((x) => x.code === this.consumptionActivityCode
            && dateTimeHelpers.getDateStringFromDateTime(x.startDateTimeLt) === isoDate
            && dateTimeHelpers.getDateStringFromDateTime(x.endDateTimeLt) === isoDate);
      },
      onSuccess(message, data) {
        this.$toast.success(message);
        this.closeFormDialog();
      },
      onError(message, data) {
        this.$toast.error(message);
      },
    },
  };
</script>

<style scoped>
  .export-btn-container {
    display: flex;
    flex-direction: row-reverse;
  }

  .cargo-icon {
    margin-left: 0 !important;
    margin-right: 12px !important;
  }

  .cargo-handling-icon {
      height: 32px !important;
      width: 32px !important;
  }

  .bulk-icon {
      top: 3px;
  }
</style>
